import { RouterProvider } from 'react-router-dom';
import { router } from './Routes';
import { AuthProvider } from '@/auth';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider } from 'posthog-js/react';
import {
  POSTHOG_API_KEY,
  isPostHogEnabled,
  posthogOptions,
} from '@/posthog-analytics/posthog';
import { PostHogUserSync } from '@/posthog-analytics/PostHogUserSync';
import { initSdk } from '@/api/setup';
import { shouldRetryQuery } from '@/api/retry';
import { AppErrorBoundary } from '@/common/components/AppErrorBoundary';
import { CookieConsentBanner } from '@/consent/CookieConsentBanner';
import { TermsConsentRecorder } from '@/consent/TermsConsentRecorder';

initSdk();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * PostHogWrapper - Wraps children with PostHogProvider on allowed domains
 * PostHog is always mounted (on allowed domains) to avoid reinitialization.
 * User identification is handled by PostHogUserSync based on auth state.
 */
function PostHogWrapper({ children }: { children: React.ReactNode }) {
  if (!isPostHogEnabled) {
    return <>{children}</>;
  }

  return (
    <PostHogProvider apiKey={POSTHOG_API_KEY} options={posthogOptions}>
      {children}
    </PostHogProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <PostHogWrapper>
        <AuthProvider>
          <PostHogUserSync />
          <TermsConsentRecorder />
          <div className="font-poppins bg-secondary">
            <AppErrorBoundary boundary="root">
              <RouterProvider router={router} />
            </AppErrorBoundary>
          </div>
          <CookieConsentBanner />
        </AuthProvider>
      </PostHogWrapper>
    </QueryClientProvider>
  );
}

App.displayName = 'App';
export default App;
